import React from 'react';
import cn from 'classnames';
import { Icon, Navigation2, getNavigationLinkClasses } from '@economic/taco';
import { Authorise, AuthorisedLink } from '@economic/react-app';
import { Trans } from '@lingui/macro';
import { useLocation } from 'react-router-dom';
import { PrimaryNavigationLinks } from './Header/Header';
import { sidebars } from '../pages';
import { useEcopediaUrl } from './Header/hooks/useEcopediaUrl';
import Intercom from '../shared/components/Intercom';

export function Sidebar({ isLargeScreen }) {
    const { pathname } = useLocation();
    const baseRoute = pathname.split('/')[1];
    const ecopediaLink = useEcopediaUrl();

    const [sidebar, setSidebar] = React.useState(baseRoute);

    // Whenever path is changed, by taking away focus for link elements and focussing body we allow taco tables to
    // hijack keyboard navigation if they want to.
    // See https://github.com/e-conomic/taco/blob/main/packages/taco/src/components/Table/hooks/useTableKeyboardNavigation.ts
    // and https://github.com/ReactTraining/react-router/issues/5210
    React.useEffect(() => {
        document.body.setAttribute('tabindex', '-1');
        document.body.focus();
        // It is important to remove the negative tabindex from body to make sure focus moves on to the next focussable
        // element within the vicinity of the click.
        document.body.removeAttribute('tabindex');

        setSidebar(pathname.split('/')[1]);
    }, [pathname]);

    const handleClick = event => {
        const page = event.currentTarget.getAttribute('href')?.replace('/', '');

        if (sidebars[page]) {
            event.preventDefault();
            setSidebar(event.currentTarget.getAttribute('href')?.replace('/', ''));
        }
    };

    const CurrentNavigation = isLargeScreen ? sidebars[baseRoute] : sidebars[sidebar];

    if (isLargeScreen) {
        return CurrentNavigation ? (
            <React.Suspense fallback={null}>
                <CurrentNavigation />
            </React.Suspense>
        ) : null;
    }

    // Admin has no primary links, so we do that a little differently - see below
    if (CurrentNavigation && (baseRoute !== 'admin' || sidebar !== 'admin')) {
        return (
            <>
                <span className="border-grey-200 flex border-b-2 py-2 px-3">
                    <button onClick={() => setSidebar('')} className={cn(getNavigationLinkClasses(), 'pl-0')}>
                        <Icon name="chevron-left" className="-mt-0.5" />
                        <Trans>Go to overview</Trans>
                    </button>
                </span>
                <React.Suspense fallback={null}>
                    <CurrentNavigation />
                </React.Suspense>
            </>
        );
    }

    const secondaryNavigation = (
        <Navigation2.Section className="!mt-auto flex-grow-0">
            <AuthorisedLink to="/inbox" as={Navigation2.Link} icon="inbox" onClick={handleClick}>
                <Trans>Inbox</Trans>
            </AuthorisedLink>
            <AuthorisedLink to="/market" as={Navigation2.Link} icon="market" onClick={handleClick}>
                <Trans>Market</Trans>
            </AuthorisedLink>
            <Authorise path="chat">
                <Intercom>
                    {({ toggle }) => (
                        <Navigation2.Link icon="chat-solid" aria-label="Chat" onClick={toggle}>
                            Chat
                        </Navigation2.Link>
                    )}
                </Intercom>
            </Authorise>
            <AuthorisedLink
                to={ecopediaLink.url}
                path="external/e-copedia"
                as={Navigation2.Link}
                aria-label={ecopediaLink.label}
                icon="e-copedia"
                target="_blank"
                tooltip={ecopediaLink.label}>
                <Trans>e-copedia</Trans>
            </AuthorisedLink>
            <AuthorisedLink to="/settings" as={Navigation2.Link} icon="settings-solid" onClick={handleClick}>
                <Trans>All settings</Trans>
                <Icon name="chevron-right" className="ml-auto -mr-1" />
            </AuthorisedLink>
            <AuthorisedLink to="/user" as={Navigation2.Link} icon="profile" onClick={handleClick}>
                <Trans>My settings</Trans>
                <Icon name="chevron-right" className="ml-auto -mr-1" />
            </AuthorisedLink>
            <Navigation2.Link href="logout" icon="log-out" onClick={handleClick}>
                <Trans>Log off</Trans>
            </Navigation2.Link>
        </Navigation2.Section>
    );

    if (baseRoute === 'admin') {
        const AdminSidebar = sidebars.admin;

        return (
            <div className="h-full flex flex-col divide-y-2 divide-grey-200">
                <div className="flex-grow overflow-auto">
                    <AdminSidebar />
                </div>
                <div className="flex-shrink-0">
                    <Navigation2>{secondaryNavigation}</Navigation2>
                </div>
            </div>
        );
    }

    return (
        <Navigation2>
            <PrimaryNavigationLinks
                as={Navigation2.Section}
                linkAs={Navigation2.Link}
                linkProps={{ name: undefined, onClick: handleClick }}
                showNesting
            />
            {secondaryNavigation}
        </Navigation2>
    );
}
